import {
  Badge,
  Button,
  ButtonProps,
  Icon,
  IconProps,
  cn,
} from '@sr/ui-library';
import React from 'react';

const EntityCardRoot = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

EntityCardRoot.displayName = 'EntityCard';

const EntityCardContent = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex items-center gap-4', className)} {...props}>
      {children}
    </div>
  );
};

EntityCardContent.displayName = 'EntityCard.Content';

interface EntityCardIconProps extends IconProps {
  classes?: {
    wrapper?: string;
  };
}

const EntityCardIcon = ({
  className,
  classes,
  icon = 'filmScript',
  ...props
}: EntityCardIconProps) => {
  return (
    <div
      className={cn(
        'flex h-16 w-12 min-w-12 items-center justify-center rounded-lg bg-muted p-2 text-muted-foreground',
        classes?.wrapper,
      )}
    >
      <Icon className={cn('h-16 w-16', className)} icon={icon} {...props} />
    </div>
  );
};

EntityCardIcon.displayName = 'EntityCard.Icon';

const EntityCardInfo = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex flex-col gap-1', className)} {...props}>
      {children}
    </div>
  );
};

EntityCardInfo.displayName = 'EntityCard.Info';

const EntityCardTitle = ({
  children,
  className,
  ...props
}: React.ComponentProps<'h3'>) => {
  return (
    <h3 className={cn('text-base font-medium', className)} {...props}>
      {children}
    </h3>
  );
};

EntityCardTitle.displayName = 'EntityCard.Title';

const EntityCardSubtitle = ({
  children,
  className,
  ...props
}: React.ComponentProps<'p'>) => {
  return (
    <p
      className={cn(
        'mb-1.5 text-xs font-normal leading-4 text-slate-600',
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
};

EntityCardSubtitle.displayName = 'EntityCard.Subtitle';

const EntityCardBadgeList = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex gap-1.5', className)} {...props}>
      {children}
    </div>
  );
};

EntityCardBadgeList.displayName = 'EntityCard.BadgeList';

const EntityCardBadge = ({
  children,
  ...props
}: React.ComponentProps<typeof Badge>) => {
  return <Badge {...props}>{children}</Badge>;
};

EntityCardBadge.displayName = 'EntityCard.Badge';

const EntityCardActions = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex items-center gap-4', className)} {...props}>
      {children}
    </div>
  );
};

EntityCardActions.displayName = 'EntityCard.Actions';

const EntityCardMetadata = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex gap-4', className)} {...props}>
      {children}
    </div>
  );
};

EntityCardMetadata.displayName = 'EntityCard.Metadata';

interface EntityCardMetadataItemProps extends React.ComponentProps<'div'> {
  label: string;
  value: number | string;
}

const EntityCardMetadataItem = ({
  className,
  label,
  value,
  ...props
}: EntityCardMetadataItemProps) => {
  return (
    <div
      className={cn('flex flex-col items-start lg:items-end', className)}
      {...props}
    >
      <span className="whitespace-nowrap text-xs font-normal leading-4 text-gray-500">
        {label}
      </span>
      <span className="text-xs font-medium leading-4">{value}</span>
    </div>
  );
};

EntityCardMetadataItem.displayName = 'EntityCard.MetadataItem';

interface EntityCardStatusProps extends React.ComponentProps<'div'> {
  label?: string;
}

const EntityCardStatus = ({
  children,
  className,
  label = 'Status',
  ...props
}: EntityCardStatusProps) => {
  return (
    <div className={cn('flex flex-col items-end', className)} {...props}>
      <span className="text-xs leading-4 text-gray-500">{label}</span>
      <span className="text-sm leading-4 text-gray-900">{children}</span>
    </div>
  );
};

EntityCardStatus.displayName = 'EntityCard.Status';

interface EntityCardSecondaryActionProps extends Omit<ButtonProps, 'icon'> {
  icon?: IconProps['icon'];
}

const EntityCardSecondaryAction = ({
  className,
  icon = 'dotsThreeVertical',
  ...props
}: EntityCardSecondaryActionProps) => {
  return (
    <Button
      className={cn('rounded-lg hover:bg-gray-100', className)}
      icon={<Icon className="text-gray-600" icon={icon} size="sm" />}
      size="smIcon"
      variant="ghost"
      {...props}
    />
  );
};

EntityCardSecondaryAction.displayName = 'EntityCard.SecondaryAction';

// Export types for TypeScript users
export type {
  EntityCardIconProps,
  EntityCardMetadataItemProps,
  EntityCardSecondaryActionProps,
  EntityCardStatusProps,
};

const EntityCard = Object.assign(EntityCardRoot, {
  Actions: EntityCardActions,
  Badge: EntityCardBadge,
  BadgeList: EntityCardBadgeList,
  Content: EntityCardContent,
  Icon: EntityCardIcon,
  Info: EntityCardInfo,
  Metadata: EntityCardMetadata,
  MetadataItem: EntityCardMetadataItem,
  SecondaryAction: EntityCardSecondaryAction,
  Status: EntityCardStatus,
  Subtitle: EntityCardSubtitle,
  Title: EntityCardTitle,
});

export { EntityCard };
